import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Heart, X, Maximize2, Minimize2, Send, Loader, Sparkles, Briefcase } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Check if user is an adult (career professional)
const isAdultUser = (role) => ['career_changer', 'professional', 'career_professional'].includes(role);

// Check if user is a parent
const isParentUser = (role) => role === 'parent';

export default function PatriciaCounselor() {
  const { user, getAuthHeader } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Listen for external open requests (from PatriciaPageGuide "Ask Patricia More")
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('open-patricia-chat', handler);
    return () => window.removeEventListener('open-patricia-chat', handler);
  }, []);

  // Determine counselor type based on user role
  const isAdult = isAdultUser(user?.role);
  const isParent = isParentUser(user?.role);
  
  // Sara for parents, Career Genie for professionals, Patricia for students
  const counselorName = isParent ? 'Sara' : (isAdult ? 'Career Genie' : 'Patricia');
  const counselorEmoji = isParent ? '💙' : (isAdult ? '✨' : '💜');

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Different greeting based on user type
      let greeting;
      
      if (isParent) {
        greeting = `Hi ${user?.full_name?.split(' ')[0] || 'there'}! 💙 I'm Sara, your AI Parent Advisor. I'm here to help you support your children's educational journey.\n\nHow can I help you today? You can ask me about:\n• Understanding your child's progress\n• College planning strategies\n• Financial planning for education\n• Supporting your child's career exploration\n• Communicating with your student`;
      } else if (isAdult) {
        greeting = `Hi ${user?.full_name?.split(' ')[0] || 'there'}! ✨ I'm your Career Genie, here to help you navigate your career transition and professional growth.\n\nHow can I help you today? You can ask me about:\n• Career transition strategies\n• Skills gap analysis\n• Resume & LinkedIn optimization\n• Interview preparation\n• Networking strategies\n• Upskilling recommendations`;
      } else {
        greeting = `Hi ${user?.full_name?.split(' ')[0] || 'there'}! 💜 I'm Patricia, your AI guidance counselor. I'm here to help you navigate your college and career journey.\n\nHow can I help you today? You can ask me about:\n• College planning & applications\n• Career path exploration\n• Scholarship opportunities\n• Action plan strategies\n• 2-week sprint planning`;
      }
      
      setMessages([{
        role: 'patricia',
        content: greeting
      }]);
    }
  }, [isOpen, user, isAdult, isParent]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/patricia-chat`, {
        message: userMessage,
        context: {
          user_name: user?.full_name,
          interests: user?.interests,
          grade_level: user?.grade_level,
          career_aspirations: user?.career_aspirations
        }
      }, getAuthHeader());

      setMessages(prev => [...prev, { 
        role: 'patricia', 
        content: response.data.response 
      }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'patricia', 
        content: "I'm having trouble connecting right now. Please try again in a moment. 💜" 
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Quick actions vary based on user type
  const studentQuickActions = [
    "Help me plan my 2-week sprint",
    "What colleges match my profile?",
    "How do I improve my profile strength?",
    "Create a study schedule for SAT prep"
  ];
  
  const adultQuickActions = [
    "Help me identify transferable skills",
    "What careers match my experience?",
    "How do I update my resume?",
    "Create a 30-day job search plan"
  ];
  
  const parentQuickActions = [
    "How can I support my child's college search?",
    "What should I know about financial aid?",
    "How do I talk to my teen about careers?",
    "What are the important deadlines to track?"
  ];
  
  const quickActions = isParent ? parentQuickActions : (isAdult ? adultQuickActions : studentQuickActions);

  if (!user) return null;

  return (
    <>
      {/* Floating Button - Bottom right circle */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          data-testid="patricia-chat-btn"
          className={`fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform group ${
            isParent
              ? 'bg-gradient-to-br from-blue-600 to-cyan-600 shadow-blue-500/30'
              : isAdult 
                ? 'bg-gradient-to-br from-emerald-600 to-teal-600 shadow-emerald-500/30'
                : 'bg-gradient-to-br from-purple-600 to-purple-700 shadow-purple-500/30'
          }`}
          title={`Chat with ${counselorName}`}
        >
          {isParent ? (
            <Heart className="w-6 h-6 text-white fill-white" />
          ) : isAdult ? (
            <Sparkles className="w-6 h-6 text-white" />
          ) : (
            <Heart className="w-6 h-6 text-white fill-white" />
          )}
          <span className="absolute -top-1 -left-1 w-4 h-4 bg-green-400 rounded-full animate-pulse border-2 border-slate-900" />
          <span className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Ask {counselorName}
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed z-50 bg-slate-900 border shadow-2xl flex flex-col transition-all duration-300 ${
            isParent
              ? 'border-blue-500/30 shadow-blue-500/20'
              : isAdult 
                ? 'border-emerald-500/30 shadow-emerald-500/20' 
                : 'border-blue-500/30 shadow-blue-500/20'
          } ${
            isFullScreen 
              ? 'inset-4 rounded-2xl' 
              : 'bottom-6 right-6 w-[380px] h-[560px] rounded-2xl'
          }`}
        >
          {/* Header - Adapts to user type */}
          <div className={`flex items-center justify-between p-4 border-b rounded-t-2xl ${
            isParent
              ? 'border-blue-500/30 bg-gradient-to-r from-blue-900/50 to-cyan-900/50'
              : isAdult 
                ? 'border-emerald-500/30 bg-gradient-to-r from-emerald-900/50 to-teal-900/50' 
                : 'border-blue-500/30 bg-gradient-to-r from-blue-900/50 to-cyan-900/50'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isParent
                  ? 'bg-gradient-to-br from-blue-500 to-cyan-600'
                  : isAdult 
                    ? 'bg-gradient-to-br from-emerald-500 to-teal-600'
                    : 'bg-gradient-to-br from-purple-500 to-pink-600'
              }`}>
                {isParent ? (
                  <Heart className="w-5 h-5 text-white fill-white" />
                ) : isAdult ? (
                  <Sparkles className="w-5 h-5 text-white" />
                ) : (
                  <Heart className="w-5 h-5 text-white fill-white" />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-white">{counselorName}</h3>
                <p className={`text-xs ${isParent ? 'text-blue-300' : (isAdult ? 'text-emerald-300' : 'text-blue-300')}`}>
                  {isParent ? 'AI Parent Advisor' : (isAdult ? 'AI Career Advisor' : 'AI Guidance Counselor')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsFullScreen(!isFullScreen)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title={isFullScreen ? 'Minimize' : 'Full screen'}
              >
                {isFullScreen ? (
                  <Minimize2 className="w-5 h-5 text-slate-300" />
                ) : (
                  <Maximize2 className="w-5 h-5 text-slate-300" />
                )}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-300" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, i) => (
              <div 
                key={i} 
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.role === 'user' 
                      ? 'bg-blue-600 text-white rounded-br-md' 
                      : 'bg-slate-800 text-slate-200 rounded-bl-md border border-blue-500/20'
                  }`}
                >
                  {msg.role === 'patricia' && (
                    <div className="flex items-center gap-2 mb-1">
                      {isAdult ? (
                        <>
                          <Sparkles className="w-3 h-3 text-emerald-400" />
                          <span className="text-xs text-emerald-400 font-medium">{counselorName}</span>
                        </>
                      ) : (
                        <>
                          <Heart className="w-3 h-3 text-purple-400 fill-purple-400" />
                          <span className="text-xs text-blue-400 font-medium">{counselorName}</span>
                        </>
                      )}
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-800 p-3 rounded-2xl rounded-bl-md border border-blue-500/20">
                  <div className="flex items-center gap-2">
                    <Loader className="w-4 h-4 text-blue-400 animate-spin" />
                    <span className="text-sm text-slate-300">{counselorName} is thinking...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2">
              <p className="text-xs text-slate-300 mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {quickActions.map((action, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setInput(action);
                      setTimeout(() => sendMessage(), 100);
                    }}
                    className="px-3 py-1.5 bg-blue-500/20 text-blue-300 text-xs rounded-full hover:bg-blue-500/30 transition-colors"
                  >
                    {action}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder={`Ask ${counselorName} anything...`}
                className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
              />
              <button
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl text-white disabled:opacity-50 hover:shadow-lg hover:shadow-blue-500/30 transition-all"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
